import React, { useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { useQueryClient } from "@tanstack/react-query";
import { message, Spin } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useTranslation } from "react-i18next";

import "dayjs/locale/pl";

import { type TeamMemberTaskWithTrainee } from "@fitness-app/data-models/entities/TeamMemberTask";

import { TEAM_MEMBER_TASKS_QUERY_KEY, useTeamMemberTasks } from "../hooks/useTeamMemberTasks";
import { useUpdateTeamMemberTask } from "../hooks/useUpdateTeamMemberTask";
import { DroppableDateSection } from "./DroppableDateSection";
import { TaskDateSection } from "./TaskDateSection";
import { TeamMemberTaskItem } from "./TeamMemberTaskItem";
import { type TaskListFilters } from "./TeamMemberTaskList";
import { WeekCalendarStrip } from "./WeekCalendarStrip";

dayjs.extend(isoWeek);
dayjs.locale("pl");

interface IncomingViewProps {
  filters: TaskListFilters;
}

export const IncomingView: React.FC<IncomingViewProps> = ({ filters }) => {
  const { t } = useTranslation("dashboard");
  const today = dayjs().startOf("day");

  const dayNamesLong = [
    t("tasks.days.monday"),
    t("tasks.days.tuesday"),
    t("tasks.days.wednesday"),
    t("tasks.days.thursday"),
    t("tasks.days.friday"),
    t("tasks.days.saturday"),
    t("tasks.days.sunday"),
  ];
  const [selectedWeekStart, setSelectedWeekStart] = useState<Dayjs>(today.startOf("isoWeek"));
  const [openSections, setOpenSections] = useState<Set<string>>(() => new Set([today.format("YYYY-MM-DD")]));
  const [activeTask, setActiveTask] = useState<TeamMemberTaskWithTrainee | null>(null);

  const queryClient = useQueryClient();
  const { mutation } = useUpdateTeamMemberTask();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const weekEnd = selectedWeekStart.add(6, "day").endOf("day");

  const { tasks, isLoading, groupTasksByWeekDay } = useTeamMemberTasks({
    startDate: selectedWeekStart.subtract(30, "day").format("YYYY-MM-DD"),
    endDate: weekEnd.format("YYYY-MM-DD"),
    includeOverdue: true,
    showCompleted: filters.showCompleted,
    traineeId: filters.traineeId,
  });

  const handleWeekChange = (weekStart: Dayjs) => {
    setSelectedWeekStart(weekStart);
  };

  const handleDateSelect = (date: Dayjs) => {
    const dateKey = date.format("YYYY-MM-DD");
    setOpenSections(new Set([dateKey]));
  };

  const handleSectionToggle = (dateKey: string, isOpen: boolean) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (isOpen) {
        next.add(dateKey);
      } else {
        next.delete(dateKey);
      }
      return next;
    });
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const task = active.data.current?.task as TeamMemberTaskWithTrainee | undefined;
    if (task) {
      setActiveTask(task);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const task = active.data.current?.task as TeamMemberTaskWithTrainee | undefined;
    const targetDate = over.data.current?.date as string | null | undefined;

    if (!task) return;

    // Don't update if dropped on the same date (including both being null)
    if (task.dueDate?.date === targetDate) return;
    if (!task.dueDate?.date && targetDate === null) return;

    const newDueDate = targetDate
      ? {
          date: targetDate,
          time: task.dueDate?.time || null,
          timezone: task.dueDate?.timezone || "Europe/Warsaw",
        }
      : null;

    // Optimistic update - immediately update cache
    queryClient.setQueriesData<TeamMemberTaskWithTrainee[]>(
      { queryKey: [TEAM_MEMBER_TASKS_QUERY_KEY], exact: false },
      (oldData) => {
        if (!oldData) return oldData;
        return oldData.map((t) => (t.id === task.id ? { ...t, dueDate: newDueDate } : t));
      },
    );

    try {
      await mutation.mutateAsync({
        id: task.id,
        data: { dueDate: newDueDate },
      });
      if (targetDate) {
        void message.success(t("tasks.taskMovedToDate", { date: dayjs(targetDate).format("D MMMM") }));
      } else {
        void message.success(t("tasks.dueDateRemoved"));
      }
    } catch {
      // Rollback on error - invalidate queries to refetch
      void queryClient.invalidateQueries({ queryKey: [TEAM_MEMBER_TASKS_QUERY_KEY] });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spin />
      </div>
    );
  }

  const { byDay, overdue, noDueDate, completed } = groupTasksByWeekDay(tasks, selectedWeekStart);

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const day = selectedWeekStart.add(i, "day");
    return {
      date: day.format("YYYY-MM-DD"),
      dayName: dayNamesLong[i] ?? "",
      tasks: byDay[day.format("YYYY-MM-DD")] || [],
      isToday: day.isSame(today, "day"),
      isPast: day.isBefore(today, "day"),
    };
  });

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="space-y-2">
        <WeekCalendarStrip
          selectedWeekStart={selectedWeekStart}
          onWeekChange={handleWeekChange}
          onDateSelect={handleDateSelect}
        />

        {overdue.length > 0 && (
          <TaskDateSection
            title={t("tasks.overdue")}
            tasks={overdue}
            isOverdue
            showAddButton={false}
            defaultOpen
            draggable
          />
        )}

        {weekDays.map((day) => {
          // Only show days from today onwards, or show past days only if they have tasks
          if (day.isPast && day.tasks.length === 0) return null;

          return (
            <DroppableDateSection
              key={day.date}
              id={day.date}
              title={day.dayName}
              date={day.date}
              tasks={day.tasks}
              isOpen={openSections.has(day.date)}
              onToggle={(isOpen) => handleSectionToggle(day.date, isOpen)}
              showAddButton={!day.isPast}
              disableDrag={day.isPast}
            />
          );
        })}

        {noDueDate.length > 0 && (
          <DroppableDateSection
            id="no-due-date"
            title={t("tasks.noDueDate")}
            date={null}
            tasks={noDueDate}
            showAddButton={false}
            defaultOpen={false}
          />
        )}

        {completed.length > 0 && (
          <TaskDateSection
            title={t("tasks.showCompleted")}
            tasks={completed}
            showAddButton={false}
            defaultOpen={false}
          />
        )}
      </div>

      <DragOverlay>
        {activeTask && (
          <div className="opacity-90 shadow-lg">
            <TeamMemberTaskItem task={activeTask} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};
